import { I18n } from '@calendly/i18n';
import { ArrowChevronDownMiniIcon, ArrowChevronUpMiniIcon } from '@calendly/ui/components/icons';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { BodyText, HeadingText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import { useEffect, forwardRef } from 'react';
import { displayAmountFromCents } from 'features/billing/helpers';
import { useBooleanState } from 'hooks/useBooleanState';
import { useToggleState } from 'hooks/useToggleState';
const stopCss = "s17103jv";
const Stop = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'stop';
  return <Component ref={ref} {...restProps} className={classNames(stopCss, props.className)} />;
});
const fadeCss = "fml6ucb";
const Fade = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(fadeCss, props.className)} />;
});
const fadeGradientCss = "f1nwosp6";
const FadeGradient = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'svg';
  return <Component ref={ref} {...restProps} className={classNames(fadeGradientCss, props.className)}>
      <>
        <linearGradient id="linear-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <Stop offset="12.5%" stopOpacity="0" />
          <Stop offset="54%" />
        </linearGradient>
        <rect fill="url(#linear-gradient)" width="100%" height="100%" />
      </>
    </Component>;
});
const flexContainerCss = "frtxb0l";
const FlexContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(flexContainerCss, props.className)} />;
});
const tieredItemContainerCss = "tu64sbl";
const TieredItemContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(tieredItemContainerCss, props.className)} />;
});
const tieredItemsWrapperShowFadeCss = "t1yvnwq8";
const tieredItemsWrapperIsExpandedCss = "t196d20q";
const tieredItemsWrapperCss = "t1jf23zp";
const TieredItemsWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    showFade,
    isExpanded,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(tieredItemsWrapperCss, {
    [tieredItemsWrapperShowFadeCss]: showFade,
    [tieredItemsWrapperIsExpandedCss]: isExpanded
  }, props.className)} />;
});
const TierLineItem = ({
  tier,
  currency,
  ...props
}) => {
  const {
    startingUnit,
    endingUnit,
    unitAmount,
    quantityUsed
  } = tier;
  const tierHeader = endingUnit ? I18n.t('publisher.billing.tiered_pricing.checkout_summary.tier_header_with_ending', {
    startingUnit,
    endingUnit
  }) : I18n.t('publisher.billing.tiered_pricing.checkout_summary.tier_header_without_ending', {
    startingUnit
  });
  const tierDescription = I18n.t('publisher.billing.tiered_pricing.checkout_summary.tier_description', {
    quantity: quantityUsed,
    cost: displayAmountFromCents(unitAmount / 12, currency, false, {
      forceDecimal: true
    })
  });
  const tierTotal = displayAmountFromCents(quantityUsed * unitAmount, currency, false);
  return <FlexContainer {...props}>
      <div>
        <BodyText weight="bold" size="medium">
          {tierHeader}
        </BodyText>
        <BodyText size="medium">
          {tierDescription}
        </BodyText>
      </div>
      <BodyText size="medium">
        {tierTotal}
      </BodyText>
    </FlexContainer>;
};
export const TieredPricingBreakdown = ({
  itemName,
  tiers,
  currency
}) => {
  const [shouldShowFade, showFade] = useBooleanState(false);
  const [isExpanded, toggleExpanded] = useToggleState(false);
  useEffect(() => {
    if (tiers && tiers.length > 2) {
      showFade();
    }
  }, [tiers, showFade]);
  return <TieredItemContainer>
      <HeadingText variant="eyebrow" level={4}>
        {itemName}
      </HeadingText>
      <TieredItemsWrapper showFade={shouldShowFade} isExpanded={isExpanded}>
        {tiers.map((tier, i) => <TierLineItem key={`tier-line-item-${tier.startingUnit}`} tier={tier} currency={currency} hidden={!isExpanded && i > 1} />)}
      </TieredItemsWrapper>
      {shouldShowFade && <Fade>
          {!isExpanded && <FadeGradient />}
          <MinimalButton fontSize="medium" onClick={toggleExpanded}>
            {I18n.t('publisher.billing.tiered_pricing.checkout_summary.see_all')}
            {isExpanded ? <ArrowChevronUpMiniIcon /> : <ArrowChevronDownMiniIcon />}
          </MinimalButton>
        </Fade>}
    </TieredItemContainer>;
};

require("./tiered_pricing_breakdown.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./tiered_pricing_breakdown.js");